import qmath from "./qmath";


class SkVertex {
	constructor() {
		this.pos  = new qmath.Vec4();
		this.tpos = new qmath.Vec4();
	}
}

class SkVertexPool {
	constructor(length) {
		length = length || 0;
		this.pool = new Array(length);

		for (let i = 0;i < length;++i) {
			this.pool[i] = new SkVertex();
		}
	}

	at(index) {
		return this.pool[index];
	}

	transformAll(mat) {
		const ls = this.pool;
		for (const v of ls) {
			v.tpos.applyM( v.pos, mat );
		}
	}

	count() {
		return this.pool.length;
	}

	refer(index) {
		if (index >= 0 && index < this.length) {
			return this.pool[index];
		} else {
			return null;
		}
	}

	bulk_set(start_index) {
		let index = start_index || 0;
		const handler = (x, y, z) => {
			const vertex = this.refer(index++);
			if (vertex) {
				vertex.pos.x = x;
				vertex.pos.y = y;
				vertex.pos.z = z;
			}

			return handler;
		};

		return handler;
	}

	copyPositions(src) {
		const n = this.count();
		for (let i = 0;i < n;++i) {
			const v = src.refer(i);
			if (v) {
				this.pool[i].pos.copyFrom(v.pos);
			}
		}
	}

	moveAll(d) {
		const n = this.count();
		for (let i = 0;i < n;++i) {
			this.pool[i].pos.add3(d);
		}
	}

	resize(newSize) {
		const oldSize = this.count();
		const added = newSize - oldSize;
		if (added === 0) { return; }

		this.pool.length = newSize;
		if (added > 0) {
			for (let i = 0;i < added;++i) {
				this.pool[oldSize + i] = new SkVertex();
			}
		}
	}
}



class SkDisplayItemBase {
	constructor() {
		this.vpool = new SkVertexPool();
		this.sortKey = 0;
		this.sortBias = 0;

		this.itemTransform = null;
		this.currentCompositeTransform = null;

		this.transformChildren = [];
		this.transformParent = null;
	}

	dispose() {
		this.transformChildren.length = 0;
		this.transformParent = null;
	}

	useLocalTransform() {
		if (!this.itemTransform) {
			this.itemTransform = new qmath.Mat4();
		}

		if (!this.currentCompositeTransform) {
			this.currentCompositeTransform = new qmath.Mat4();
		}
	}

	copyToLocalTransform(srcMat) {
		this.itemTransform?.copyFrom(srcMat);
	}

	processTransformTree(globalTransform) {
		if (!this.transformParent && // only if this node have NO parent = top level
			this.transformChildren.length > 0) {
			
			for (const ch of this.transformChildren) {
				ch.#updateLocalTransformIf(globalTransform);
			}
		}
	}

	#updateLocalTransformIf(parentTransform) {
		if (this.itemTransform && this.currentCompositeTransform && this.transformParent) {
			qmath.bulkApplyMatrices(this.currentCompositeTransform, [this.itemTransform, parentTransform]);

			if (this.transformChildren) {
				for (const ch of this.transformChildren) {
					ch.#updateLocalTransformIf(this.currentCompositeTransform);
				}	
			}
		}
	}

	addTransformChild(d) {
		if (this.transformChildren.indexOf(d) < 0) {
			this.transformChildren.push(d);
			d.transformParent = this;
		}
	}

	clearTransformChildren() {
		this.transformChildren.length = 0;
	}

	calcSortKey() {}

	draw(_g, _pixelRatio) {
	}

	applyFinalTransform(globalAll) {
		const t = this.currentCompositeTransform ?? globalAll;
		this.vpool.transformAll(t);
	}
}

const kOtFillPreset = "rgba(240,240,240,0.85)";
const kInFillPreset = "rgba(80,80,80,0.2)";

class SkPolygonDisplayItem extends SkDisplayItemBase {
	constructor() {
		super();
		this.fillStyle = kOtFillPreset;
		this.withShadow = 0;
		this.closed = true;

		this.loopLengthList = [];
	}

	calcSortKey() {
		this.sortKey = this.sortBias + calc_polygon_representative_point(this.vpool, this.loopLengthList);
	}

	draw(g, pixelRatio) {
		g.save();
		g.fillStyle = this.fillStyle;
		g.strokeStyle = "#777";
		g.lineWidth = pixelRatio;

		let readPos = 0;		
		g.beginPath();
		for (const lplen of this.loopLengthList) {
			for (let i = 0;i < lplen;++i) {
				const v = this.vpool.at(readPos++);
				const tp = v.tpos;

				if (0 === i) {
					g.moveTo(tp.x, tp.y);
				} else {
					g.lineTo(tp.x, tp.y);
				}
			}

			g.closePath();
		}

		if (this.withShadow) {
			g.save();

			// Safari対策としてシャドウでBlurフィルタの代わりをする
			g.fillStyle = "#fff";
			g.globalCompositeOperation = "multiply";
			g.shadowBlur = pixelRatio * (this.withShadow === 2) ? 20 : 8;
			g.shadowColor = "rgba(0,0,0,0.4)";
			g.shadowOffsetY = pixelRatio;
			g.fill("evenodd");

			g.restore();
		}

		g.fill("evenodd");
		g.stroke();
		g.restore();
	}

	setOuterPolygonPreset() {
		this.fillStyle = kOtFillPreset;
	}

	setInnerPolygonPreset() {
		this.fillStyle = kInFillPreset;		
	}
}

const NodeAppearance = {
	Elevator: 0
};

class SkNodeDisplayItem extends SkDisplayItemBase {
	constructor() {
		super();

		this.appearance = NodeAppearance.Elevator;
	}

	draw(g, pixelRatio) {
		g.save();

		if (this.appearance === NodeAppearance.Elevator) {
			this.drawElevator(g, pixelRatio);
		}

		g.restore();
	}

	drawElevator(g, pixelRatio) {
		const vp = this.vpool;
		if (vp.count() < 2) { return; }

		const v1 = vp.at(0).tpos;
		const v2 = vp.at(1).tpos;

		set_black_shadow(g, pixelRatio);
		g.setLineDash([pixelRatio*4, pixelRatio*2]);

		g.beginPath();
		g.moveTo(v1.x, v1.y);
		g.lineTo(v2.x, v2.y);

		g.stroke();
	}
}

class SkStairsDisplayItem extends SkDisplayItemBase {
	calcSortKey() {
		this.sortKey = this.sortBias;

		this.vUp  = new qmath.Vec4();
		this.vFwd = new qmath.Vec4();
		this.vAC  = new qmath.Vec4();
	}

	draw(g, pixelRatio) {
		g.save();

		const vp = this.vpool;
		if (vp.count() < 3) { return; }

		const v1 = vp.at(0).tpos;
		const v2 = vp.at(1).tpos;
		const v3 = vp.at(2).tpos;

		this.vUp.diff2(v2, v3);
		this.vFwd.diff2(v1, v2);
		this.vAC.diff2(v1, v3);

		const distance = this.vAC.calcLen2() / pixelRatio;
		const nSteps = (distance / 19) + 1;

		g.lineWidth = pixelRatio;
		g.strokeStyle = "#FFF";
		set_black_shadow(g, pixelRatio);
		renderStairLine(g, v1, this.vUp, this.vFwd, nSteps);

		g.restore();
	}
}

function renderStairLine(g, beginPos, vUp, vFwd, nSteps) {
	const f_sx = vFwd.x / nSteps;
	const f_sy = vFwd.y / nSteps;
	const u_sx = vUp.x / nSteps;
	const u_sy = vUp.y / nSteps;

	let x = beginPos.x;
	let y = beginPos.y;

	g.beginPath();
	for (let i = 0;i < nSteps;++i) {
		if (0 === i) {
			g.moveTo(x, y);
		} else {
			g.lineTo(x, y);
		}

		x += u_sx;
		y += u_sy;
		g.lineTo(x, y);

		x += f_sx;
		y += f_sy;
	}	
	g.stroke();
}


const kInvalidCoord = -9999;
function calc_polygon_representative_point(vpool, loopLengthList) {
	if (loopLengthList.length < 1) { return kInvalidCoord; }

	let sum = 0;
	const len = loopLengthList[0];
	for (let i = 0;i < len;++i) {
		const v = vpool.at(i);
		sum += v.tpos.z;
	}

	return sum / len;
}

function set_black_shadow(g, pixelRatio) {
	g.lineWidth = pixelRatio * 2;
	g.strokeStyle = "#fff";
	g.shadowBlur = pixelRatio;
	g.shadowColor = "#000";
}

export { SkPolygonDisplayItem, SkNodeDisplayItem, SkDisplayItemBase, SkStairsDisplayItem };